import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from '@/components/base/StatusBadge';
import ProgressBar from '@/components/base/ProgressBar';
import { api } from '@/utils/api';
import { formatMoney } from '@/utils/format';

interface CreatorCampaign {
  rosterId: string;
  campaignId: string;
  campaignName: string;
  campaignStatus: string;
  status: string;
  rate: number;
  currency: string;
  deliverablesDone: number;
  deliverablesTotal: number;
}

/** The creator's campaigns with this agency (GET /creators/:id/campaigns), newest first. */
export default function CampaignHistoryPanel({ creatorId }: { creatorId: string }) {
  const [rows, setRows] = useState<CreatorCampaign[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError('');
    api<CreatorCampaign[]>(`/creators/${creatorId}/campaigns`)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((loadError: Error) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId]);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-3">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">Campaign history</h2>
          <p className="mt-0.5 text-xs text-foreground-500">Their campaigns with your agency</p>
        </div>
        {rows ? <span className="text-xs text-foreground-500">{rows.length} campaign{rows.length === 1 ? '' : 's'}</span> : null}
      </header>
      {error ? (
        <p role="alert" className="px-5 py-4 text-sm text-primary-700">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </p>
      ) : rows === null ? (
        <p className="px-5 py-4 text-sm text-foreground-500">Loading campaigns…</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-foreground-500">Not on any of your campaigns yet.</p>
      ) : (
        <ul className="divide-y divide-background-100">
          {rows.map((row) => (
            <li key={row.rosterId}>
              <Link
                to={`/campaigns/${row.campaignId}?tab=creators`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 hover:bg-background-100"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground-900">{row.campaignName}</p>
                  <p className="text-xs text-foreground-500">
                    Campaign {row.campaignStatus.toLowerCase()} · {formatMoney(row.rate, row.currency)}
                  </p>
                </div>
                <StatusBadge status={row.status} />
                <div className="w-28">
                  {row.deliverablesTotal > 0 ? (
                    <ProgressBar value={(row.deliverablesDone / row.deliverablesTotal) * 100} showLabel />
                  ) : (
                    <span className="text-xs text-foreground-400">No content yet</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
